/**
 * Utilities for converting between stored foto URLs and Supabase storage paths.
 *
 * `rapport_details.fotos.remote_url` may contain:
 * - a Supabase storage URL/path
 * - a Firebase migration URL that should be used as-is
 *
 * These helpers provide backward-compatible handling of both formats.
 */

const PUBLIC_STORAGE_PATH_RE = /^https?:\/\/[^/]+\/storage\/v1\/object\/public\/userFiles\/(.+)$/;
const LEGACY_STORAGE_PATH_RE = /^https?:\/\/[^/]+\/storage\/v1\/object\/userFiles\/(.+)$/;
const SIGNED_STORAGE_PATH_RE = /^https?:\/\/[^/]+\/storage\/v1\/object\/sign\/userFiles\/(.+)$/;
const RELATIVE_PUBLIC_STORAGE_PATH_RE = /^\/?storage\/v1\/object\/public\/userFiles\/(.+)$/;
const RELATIVE_LEGACY_STORAGE_PATH_RE = /^\/?storage\/v1\/object\/userFiles\/(.+)$/;
const RELATIVE_SIGNED_STORAGE_PATH_RE = /^\/?storage\/v1\/object\/sign\/userFiles\/(.+)$/;

function normalizeStoragePath(path: string): string {
  return path.replace(/^\/+/, "").trim();
}

function isHttpUrl(value: string): boolean {
  return value.startsWith("http://") || value.startsWith("https://");
}

function getStorageBaseUrl(): string | null {
  const storageUrl = import.meta.env.VITE_SUPABASE_STORAGE_URL as string | undefined;
  if (storageUrl) {
    return storageUrl.replace(/\/+$/, "");
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!supabaseUrl) return null;

  return `${new URL(supabaseUrl).origin}/storage/v1`;
}

/**
 * Extract the relative storage path from a full Supabase Storage URL.
 * If the input is already a relative path, returns it unchanged.
 */
export function parseStoragePath(urlOrPath: string): string {
  const input = urlOrPath.trim();
  const match =
    input.match(PUBLIC_STORAGE_PATH_RE) ||
    input.match(LEGACY_STORAGE_PATH_RE) ||
    input.match(SIGNED_STORAGE_PATH_RE) ||
    input.match(RELATIVE_PUBLIC_STORAGE_PATH_RE) ||
    input.match(RELATIVE_LEGACY_STORAGE_PATH_RE) ||
    input.match(RELATIVE_SIGNED_STORAGE_PATH_RE);

  if (!match) return input;

  const [pathOnly] = match[1].split("?");
  return normalizeStoragePath(pathOnly);
}

export function isSupabaseStorageReference(urlOrPath: string): boolean {
  const input = urlOrPath.trim();
  if (!input) return false;

  return Boolean(
    input.match(PUBLIC_STORAGE_PATH_RE) ||
      input.match(LEGACY_STORAGE_PATH_RE) ||
      input.match(SIGNED_STORAGE_PATH_RE) ||
      input.match(RELATIVE_PUBLIC_STORAGE_PATH_RE) ||
      input.match(RELATIVE_LEGACY_STORAGE_PATH_RE) ||
      input.match(RELATIVE_SIGNED_STORAGE_PATH_RE) ||
      (!isHttpUrl(input) &&
        !input.startsWith("data:") &&
        !input.startsWith("blob:") &&
        !input.startsWith("capacitor://") &&
        !input.startsWith("file://"))
  );
}

/**
 * Construct a full public Supabase Storage URL from a relative path or full URL.
 * Backward-compatible: handles both legacy full URLs and new relative paths.
 */
export function resolveStorageUrl(urlOrPath: string): string {
  if (!urlOrPath) return urlOrPath;

  const input = urlOrPath.trim();
  if (!input) return input;

  if (
    input.startsWith("data:") ||
    input.startsWith("blob:") ||
    input.startsWith("capacitor://") ||
    input.startsWith("file://")
  ) {
    return input;
  }

  const relativePath = parseStoragePath(input);
  const normalizedRelativePath = normalizeStoragePath(relativePath);
  const storageBaseUrl = getStorageBaseUrl();

  if (storageBaseUrl && !isHttpUrl(relativePath)) {
    return `${storageBaseUrl}/object/public/userFiles/${normalizedRelativePath}`;
  }

  if (!isHttpUrl(input)) {
    return `/storage/v1/object/public/userFiles/${normalizedRelativePath}`;
  }

  if (storageBaseUrl) {
    const parsedRelativePath = parseStoragePath(input);
    if (!isHttpUrl(parsedRelativePath)) {
      return `${storageBaseUrl}/object/public/userFiles/${normalizeStoragePath(parsedRelativePath)}`;
    }
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!supabaseUrl) return input;

  const origin = new URL(supabaseUrl).origin;

  if (input.includes("127.0.0.1")) {
    return input.replace("http://127.0.0.1:54321", origin);
  }

  return input;
}
